import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import api, { downloadFile } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Play, Lock, Send, Eye, ChevronRight, Download } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function PayrollPage() {
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runOpen, setRunOpen] = useState(false);
  const [runMonth, setRunMonth] = useState(String(new Date().getMonth() + 1));
  const [runYear, setRunYear] = useState(String(new Date().getFullYear()));
  const [running, setRunning] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [payslipHtml, setPayslipHtml] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const fetchPayrolls = useCallback(() => {
    api.get('/payroll')
      .then(res => setPayrolls(res.data))
      .catch(() => toast.error('Failed to load payrolls'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPayrolls(); }, [fetchPayrolls]);

  const handleRun = async () => {
    setRunning(true);
    try {
      await api.post('/payroll/run', { month: parseInt(runMonth), year: parseInt(runYear) });
      toast.success('Payroll generated successfully');
      setRunOpen(false);
      fetchPayrolls();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run payroll');
    } finally {
      setRunning(false);
    }
  };

  const openDetail = async (payroll) => {
    setSelectedPayroll(payroll);
    setDetailOpen(true);
    try {
      const res = await api.get(`/payroll/${payroll.id}`);
      setPayslips(res.data.payslips);
    } catch {
      toast.error('Failed to load payslips');
    }
  };

  const handleDownloadPayslip = async (ps) => {
    try {
      await downloadFile(
        `/payslips/${ps.id}/download`,
        `payslip_${ps.employee_id}_${MONTHS[ps.month - 1]}_${ps.year}.pdf`
      );
      toast.success('Payslip downloaded');
    } catch {
      toast.error('Download failed');
    }
  };

  const handleFinalize = async () => {
    setActionLoading('finalize');
    try {
      await api.post(`/payroll/${selectedPayroll.id}/finalize`);
      toast.success('Payroll finalized');
      setSelectedPayroll(p => ({ ...p, status: 'finalized' }));
      fetchPayrolls();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to finalize');
    } finally {
      setActionLoading('');
    }
  };

  const handleSendPayslips = async () => {
    setActionLoading('send');
    try {
      const res = await api.post(`/payroll/${selectedPayroll.id}/send-payslips`);
      toast.success(res.data.message);
      if (res.data.errors?.length) {
        res.data.errors.forEach(e => toast.error(e));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send payslips');
    } finally {
      setActionLoading('');
    }
  };

  const viewPayslip = async (payslipId) => {
    try {
      const res = await api.get(`/payslips/${payslipId}`);
      setPayslipHtml(res.data.html);
      setViewOpen(true);
    } catch {
      toast.error('Failed to load payslip');
    }
  };

  return (
    <div data-testid="payroll-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Payroll</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Process and manage monthly payroll</p>
        </div>
        <Button onClick={() => setRunOpen(true)} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="run-payroll-btn">
          <Play className="w-4 h-4 mr-2" strokeWidth={1.5} /> Run Payroll
        </Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest">Period</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Employees</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Total Amount</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Created By</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading...</TableCell></TableRow>
            ) : payrolls.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No payroll runs yet</TableCell></TableRow>
            ) : payrolls.map(p => (
              <TableRow key={p.id} data-testid={`payroll-row-${p.id}`}>
                <TableCell className="font-mono text-sm">{MONTHS[p.month - 1]} {p.year}</TableCell>
                <TableCell className="font-mono text-sm">{p.employee_count}</TableCell>
                <TableCell className="font-mono text-sm font-medium">₹{p.total_amount?.toLocaleString('en-IN')}</TableCell>
                <TableCell>
                  <Badge className={p.status === 'finalized' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                    {p.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.created_by}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openDetail(p)} data-testid={`view-payroll-${p.id}`}>
                    View <ChevronRight className="w-3 h-3 ml-1" strokeWidth={1.5} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Run Payroll Dialog */}
      <Dialog open={runOpen} onOpenChange={setRunOpen}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Run Payroll</DialogTitle>
            <DialogDescription>Select the month and year to generate payroll.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Month</Label>
              <Select value={runMonth} onValueChange={setRunMonth}>
                <SelectTrigger className="rounded-sm" data-testid="payroll-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Year</Label>
              <Select value={runYear} onValueChange={setRunYear}>
                <SelectTrigger className="rounded-sm" data-testid="payroll-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleRun} disabled={running} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="confirm-run-payroll-btn">
              {running ? 'Processing...' : 'Generate Payroll'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl rounded-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Payroll - {selectedPayroll && `${MONTHS[selectedPayroll.month - 1]} ${selectedPayroll.year}`}
            </DialogTitle>
            <DialogDescription>
              {selectedPayroll?.employee_count} employees | Total: ₹{selectedPayroll?.total_amount?.toLocaleString('en-IN')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mb-4">
            {selectedPayroll?.status === 'draft' && (
              <Button onClick={handleFinalize} disabled={actionLoading === 'finalize'} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="finalize-payroll-btn">
                <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {actionLoading === 'finalize' ? 'Finalizing...' : 'Finalize Payroll'}
              </Button>
            )}
            {selectedPayroll?.status === 'finalized' && (
              <Button onClick={handleSendPayslips} disabled={actionLoading === 'send'} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="send-payslips-btn">
                <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {actionLoading === 'send' ? 'Sending...' : 'Send Payslips via Email'}
              </Button>
            )}
          </div>

          <div className="border border-border rounded-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Days</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Gross</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Deductions</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Net Pay</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.map(ps => (
                  <TableRow key={ps.id} data-testid={`payslip-row-${ps.employee_id}`}>
                    <TableCell className="font-medium">{ps.employee_name}</TableCell>
                    <TableCell className="font-mono text-xs">{ps.employee_id}</TableCell>
                    <TableCell className="font-mono text-xs">{ps.present_days}/{ps.working_days}</TableCell>
                    <TableCell className="font-mono text-xs">₹{ps.total_earnings?.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="font-mono text-xs text-red-600">-₹{ps.total_deductions?.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="font-mono text-sm font-medium">₹{ps.net_pay?.toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      {ps.email_sent ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Sent</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => viewPayslip(ps.id)} data-testid={`view-payslip-${ps.employee_id}`}>
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPayslip(ps)} data-testid={`download-payslip-${ps.employee_id}`} className="text-[#002FA7]">
                          <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payslip HTML View */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Payslip Preview</DialogTitle>
            <DialogDescription>Generated payslip document</DialogDescription>
          </DialogHeader>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(payslipHtml) }} className="border border-border rounded-sm" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
